// Allow async polyfill
import "core-js/stable";
import "regenerator-runtime/runtime";
import { ImageSourceSetData } from "../common/classes/ImageSourceSet";

import { getValEmail } from "../services/api/user/getValEmail";

export enum ValType {
  NotNull,
  PasswordFormat,
  PasswordMatches,
  Email,
  DisplayName,
  ValRegister,
  Twitter,
  Instagram,
  Facebook,
  Youtube,
  Twitch,
  Phone,
  EmailFormat,
  URL,
  File,
  FileTotalSize,
  PositiveInt,
}

export type FieldState = {
  [key: string]: [string | File, boolean];
};

export async function validate(
  value: string | number | boolean | File,
  val_type: ValType,
  ...args: Array<string | number | boolean>
) {
  switch (val_type) {
    case ValType.NotNull:
      return valNotNull(value);
    case ValType.PasswordFormat:
      if (typeof value !== "string") {
        return false;
      }

      return valPasswordFormat(value);
    case ValType.PasswordMatches:
      return valPasswordMatch(value, ...args);
    case ValType.Email:
      if (typeof value !== "string") {
        return false;
      }

      try {
        const result = await valEmail(value);
        return result;
      } catch (error) {
        return null;
      }
    case ValType.DisplayName:
      if (typeof value !== "string") {
        return false;
      }

      return valDisplayName(value);

    case ValType.ValRegister:
      if (typeof value !== "string") {
        return false;
      }

      return valRegisteredEmail(value);

    case ValType.Twitter:
      if (typeof value !== "string") {
        return false;
      } else if (value === "") {
        return true;
      }

      return valTwitter(value);

    case ValType.Instagram:
      if (typeof value !== "string") {
        return false;
      } else if (value === "") {
        return true;
      }

      return valInstagram(value);

    case ValType.Facebook:
      if (typeof value !== "string") {
        return false;
      } else if (value === "") {
        return true;
      }

      return valFacebook(value);

    case ValType.Youtube:
      if (typeof value !== "string") {
        return false;
      } else if (value === "") {
        return true;
      }

      return valYoutube(value);

    case ValType.Twitch:
      if (typeof value !== "string") {
        return false;
      } else if (value === "") {
        return true;
      }

      return valTwitch(value);

    case ValType.Phone:
      if (typeof value !== "string") {
        return false;
      } else if (value === "") {
        return true;
      }

      return valPhoneNumber(value);
    case ValType.EmailFormat:
      if (typeof value !== "string") {
        return false;
      }

      return valEmailFormat(value);
    case ValType.URL:
      if (typeof value !== "string") {
        return false;
      }

      return valURL(value);
    case ValType.File:
      if (!(value instanceof File)) {
        return false;
      }

      return valFile(value);
    case ValType.PositiveInt:
      if (typeof value !== "string") {
        return false;
      }

      return valPositiveInt(value);
    default:
      return true;
  }
}

export function valNotNull(value: string | number | boolean | File) {
  if (value === 0) {
    return true;
  } else if (value == null || value == undefined || value == "") {
    return false;
  } else {
    return true;
  }
}

function valPasswordFormat(value: string) {
  if (value.length < 8) {
    return false;
  }
  const pass_regex =
    /(?=.*[a-z])(?=.*[A-Z])(?=.*[\d])(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}/;
  return pass_regex.test(value);
}

function valPasswordMatch(
  value: string | number | boolean | File,
  ...args: Array<string | number | boolean>
) {
  let matched = false;

  for (const arg of args) {
    if (arg === value) {
      matched = true;
      break;
    }
  }

  return matched;
}

async function valEmail(value: string) {
  const email_regex = /[\S]+[@]+[\S]+\.+[\S]{1,}/;
  if (email_regex.test(value) === false) {
    return false;
  }

  try {
    const result = await getValEmail(value);
    if (result !== false) {
      return result.data.result;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

function valDisplayName(value: string) {
  if (value.length > 64) {
    return false;
  } else {
    return true;
  }
}

async function valRegisteredEmail(value: string) {
  const email_regex = /[\S]+[@]+[\S]+\.+[\S]{1,}/;
  if (email_regex.test(value) === false) {
    return false;
  }

  try {
    const result = await getValEmail(value);
    if (result !== false) {
      return !result.data.result;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

function valTwitter(value: string) {
  const twitter_regex =
    /(https|http):\/\/(www\.)?(twitter\.com|x.com)\/+[a-zA-Z0-9_]{4,15}$/;
  if (twitter_regex.test(value) === false) {
    return false;
  } else {
    return true;
  }
}

function valInstagram(value: string) {
  const instagram_regex = /(https|http):\/\/(www\.)?instagram\.com\/+[\S]{1,}/;
  if (instagram_regex.test(value) === false) {
    return false;
  } else {
    return true;
  }
}

function valFacebook(value: string) {
  const facebook_regex = /(https|http):\/\/(www\.)?facebook\.com\/+[\S]{1,}/;
  if (facebook_regex.test(value) === false) {
    return false;
  } else {
    return true;
  }
}

export function valYoutubeChannelBaseURL(value: string) {
  const youtube_default_url_regex =
    /(https|http):\/\/(www\.)?youtube\.com\/channel\/$/;

  return youtube_default_url_regex.test(value);
}

function valYoutube(value: string) {
  const youtube_channel_regex =
    /(https|http):\/\/(www\.)?youtube\.com\/channel\/\S{0,}/;

  if (youtube_channel_regex.test(value) === false) {
    return false;
  } else {
    return true;
  }
}

function valTwitch(value: string) {
  const twitch_regex = /(https|http):\/\/(www\.)?twitch\.tv\/+[\S]{1,}/;
  if (twitch_regex.test(value) === false) {
    return false;
  } else {
    return true;
  }
}

function valPhoneNumber(value: string) {
  const phone_regex =
    /^(?:\d{10}|\d{11}|\d{3}-\d{3}-\d{4}|\d{2}-\d{4}-\d{4}|\d{4}-\d{2}-\d{4}|\d{4}-\d{3}-\d{3}|\d{3}-\d{4}-\d{4})$/;
  if (phone_regex.test(value) === false) {
    return false;
  } else {
    return true;
  }
}

function valEmailFormat(value: string) {
  const email_regex = /[\S]+[@]+[\S]+\.+[\S]{1,}/;
  if (email_regex.test(value) === false) {
    return false;
  } else {
    return true;
  }
}

export function valURL(value: string) {
  if (value) {
    try {
      const url = new URL(value);
      if (url.protocol === "http:" || url.protocol === "https:") {
        const domain_name = url.hostname.replace("www.", "");
        const domain_pattern = new RegExp(
          /^([a-z\d-]{1,63}\.{1,1})+[a-z]{2,63}$/,
          "i"
        );
        return domain_pattern.test(domain_name);
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
}

export const valFile = (value: File) => {
  // 10MB以内
  if (value.size > Math.pow(1024, 2) * 10) {
    return false;
  }
  return true;
};

export const valFileTotalSize = (imgs: Array<File | ImageSourceSetData>) => {
  // 10MB以内
  let totalSizes: number = 0;
  imgs.forEach((img) => {
    if (img instanceof File) {
      totalSizes += img.size;
    }
  });

  return totalSizes > Math.pow(1024, 2) * 10 ? false : true;
};

export const valPositiveInt = (value: string) => {
  if (value === "") {
    return true;
  }

  return /^[1-9][0-9]*$/.test(value);
};

export function getErrorMessage(val_type?: ValType) {
  switch (val_type) {
    case ValType.NotNull:
      // TODO: Translate
      return "この項目が入力されていないようです。ご入力ください。";
    case ValType.PasswordFormat:
      // TODO: Translate
      return "パスワードは8文字以上、全て半角で【アルファベット大文字】【アルファベット小文字】【数字】【特殊記号(!@#$%^&*)】をそれぞれ1文字以上含む必要があります。";
    case ValType.PasswordMatches:
      // TODO: Translate
      return "同じパスワードが入力されていないようです。";
    case ValType.Email:
      // TODO: Translate
      return "入力したメールアドレスは既に登録されているか、メールアドレスではない形式のようです。正しいメールアドレスをご入力ください。";
    case ValType.DisplayName:
      // TODO: Translate
      return "名前が長すぎるようです。名前は24文字以内でご入力ください。";
    case ValType.ValRegister:
      // TODO: Translate
      return "ご入力いただいたメールアドレスが正しくない、もしくは登録されておりません。";
    case ValType.Twitter:
    case ValType.Instagram:
    case ValType.Facebook:
    case ValType.Youtube:
    case ValType.Twitch:
      // TODO: Translate
      return "ご入力されているURLの形式が正しくありません。";
    case ValType.Phone:
      return "有効な電話番号を入力して下さい。";
    case ValType.EmailFormat:
      return "メールアドレスではない形式のようです。正しいメールアドレスをご入力ください。";
    case ValType.URL:
      return "URLではない形式のようです。正しいURLをご入力ください。";
    case ValType.File:
      return "ファイルサイズが大きすぎます。";
    case ValType.FileTotalSize:
      return "画像の合計サイズがアップロード上限を超えています。";
    case ValType.PositiveInt:
      return "1以上の半角数値を入力してください。";
    default:
      // TODO: Translate
      return "不明なエラーが発生しました。";
  }
}
